import { useState } from 'react'
import { useFormContext, type RegisterOptions } from 'react-hook-form'
import { LuEye, LuEyeOff } from 'react-icons/lu'
import { cn } from '#/lib/utils'

type FormInputProps = {
  name: string
  label: string
  placeholder?: string
  type?: string
  icon?: React.ReactNode
  rules?: RegisterOptions
  className?: string
  /**
   * 'dark' — for noir/dark-bg sections (default, existing behaviour)
   * 'light' — for white/light-bg cards (auth pages, etc.)
   */
  variant?: 'dark' | 'light'
}

export function FormInput({
  name,
  label,
  placeholder,
  type = 'text',
  icon,
  rules,
  className,
  variant = 'light',
}: FormInputProps) {
  const {
    register,
    formState: { errors },
  } = useFormContext()

  const [showPassword, setShowPassword] = useState(false)
  const isPassword = type === 'password'
  const resolvedType = isPassword ? (showPassword ? 'text' : 'password') : type

  const error = errors[name]?.message as string | undefined

  const isLight = variant === 'light'

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <label
        htmlFor={name}
        className={cn(
          'text-sm font-medium',
          isLight ? 'text-neutral-700' : 'text-neutral-300',
        )}
      >
        {label}
        {rules?.required && <span className="ml-1 text-primary-500">*</span>}
      </label>

      <div
        className={cn(
          'relative flex items-center overflow-hidden rounded-xl border transition-all duration-200',
          isLight
            ? [
                'bg-white',
                'focus-within:border-primary-500 focus-within:ring-3 focus-within:ring-primary-500/15',
                error
                  ? 'border-red-400 ring-2 ring-red-400/15'
                  : 'border-neutral-200 hover:border-neutral-300',
              ]
            : [
                'bg-white/5',
                'focus-within:border-primary-500/60 focus-within:shadow-[0_0_0_3px_rgba(255,182,6,0.10)]',
                error
                  ? 'border-red-500/60'
                  : 'border-white/10 hover:border-white/20',
              ],
        )}
      >
        {icon && (
          <span
            className={cn(
              'pointer-events-none absolute left-4 text-base',
              isLight ? 'text-neutral-400' : 'text-neutral-400',
            )}
          >
            {icon}
          </span>
        )}

        <input
          id={name}
          type={resolvedType}
          placeholder={placeholder}
          {...register(name, rules)}
          className={cn(
            'h-12 w-full bg-transparent pr-4 text-sm outline-none',
            'placeholder:text-neutral-400',
            isLight
              ? 'text-neutral-900'
              : 'text-white placeholder:text-neutral-500',
            icon ? 'pl-11' : 'pl-4',
            isPassword ? 'pr-11' : '',
          )}
        />

        {/* Password visibility toggle */}
        {isPassword && (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShowPassword((v) => !v)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
            className={cn(
              'absolute right-4 transition-colors',
              isLight
                ? 'text-neutral-400 hover:text-neutral-700'
                : 'text-neutral-500 hover:text-neutral-300',
            )}
          >
            {showPassword ? (
              <LuEyeOff className="text-base" />
            ) : (
              <LuEye className="text-base" />
            )}
          </button>
        )}
      </div>

      {error && (
        <p
          role="alert"
          className={cn(
            'text-xs font-medium',
            isLight ? 'text-red-500' : 'text-red-400',
          )}
        >
          {error}
        </p>
      )}
    </div>
  )
}
